import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const OFFSET = 8;

/**
 * CustomBubbleMenu - Custom bubble menu for TipTap editor.
 * Replaces TipTap's BubbleMenu to eliminate tippy.js dependency.
 */
const CustomBubbleMenu = memo(({ children, className, editor }) => {
    const menuRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ top: -9999, left: -9999 });

    const updatePosition = useCallback(() => {
        if (!editor) return;

        const { state, view } = editor;
        const { from, to, empty } = state.selection;

        // Hide if selection is empty or cursor (no range selected)
        if (empty) {
            setIsVisible(false);
            return;
        }

        // Get the coordinates of the selection (viewport-relative)
        const start = view.coordsAtPos(from);
        const end = view.coordsAtPos(to);

        // Calculate the selection bounds
        const selectionRect = {
            top: Math.min(start.top, end.top),
            bottom: Math.max(start.bottom, end.bottom),
            left: Math.min(start.left, end.left),
            right: Math.max(start.right, end.right),
        };

        // We need to show the menu first to measure it
        setIsVisible(true);

        // Use requestAnimationFrame to ensure the menu is rendered before measuring
        requestAnimationFrame(() => {
            if (!menuRef.current) return;

            const menuRect = menuRef.current.getBoundingClientRect();
            const { innerWidth, innerHeight } = window;

            // Position above the selection, centered (viewport coordinates for position: fixed)
            let top = selectionRect.top - menuRect.height - OFFSET;
            let left =
                selectionRect.left +
                (selectionRect.right - selectionRect.left) / 2 -
                menuRect.width / 2;

            // Boundary checks
            // If menu goes above viewport, position below selection
            if (top < OFFSET) {
                top = selectionRect.bottom + OFFSET;
            }

            // If menu goes below viewport, keep it at the bottom
            if (top + menuRect.height > innerHeight - OFFSET) {
                top = innerHeight - menuRect.height - OFFSET;
            }

            // Keep within horizontal bounds
            if (left < OFFSET) {
                left = OFFSET;
            } else if (left + menuRect.width > innerWidth - OFFSET) {
                left = innerWidth - menuRect.width - OFFSET;
            }

            setPosition({ top, left });
        });
    }, [editor]);

    // Store updatePosition in a ref to avoid effect re-runs when callback changes
    // Ref to always access the latest updatePosition without adding it to effect dependencies
    const updatePositionRef = useRef();
    updatePositionRef.current = updatePosition;

    useEffect(() => {
        if (!editor) return;

        const handleSelectionUpdate = () => {
            requestAnimationFrame(() => updatePositionRef.current());
        };

        const handleBlur = () => setIsVisible(false);

        editor.on('selectionUpdate', handleSelectionUpdate);
        editor.on('focus', handleSelectionUpdate);
        editor.on('blur', handleBlur);

        return () => {
            editor.off('selectionUpdate', handleSelectionUpdate);
            editor.off('focus', handleSelectionUpdate);
            editor.off('blur', handleBlur);
        };
    }, [editor]);

    // Update position on scroll/resize
    useEffect(() => {
        if (!isVisible) return;

        const handleUpdate = () => updatePositionRef.current();

        window.addEventListener('scroll', handleUpdate, true);
        window.addEventListener('resize', handleUpdate);

        return () => {
            window.removeEventListener('scroll', handleUpdate, true);
            window.removeEventListener('resize', handleUpdate);
        };
    }, [isVisible]);

    // Initial position update when editor is ready
    useLayoutEffect(() => {
        if (editor) {
            updatePositionRef.current();
        }
    }, [editor]);

    if (!editor) return null;

    // Use portal to render at body level, avoiding overflow issues
    return createPortal(
        <div
            ref={menuRef}
            className={className}
            style={{
                position: 'fixed',
                top: `${position.top}px`,
                left: `${position.left}px`,
                zIndex: 9999,
                visibility: isVisible ? 'visible' : 'hidden',
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 100ms',
            }}
        >
            {children}
        </div>,
        document.body,
    );
});

CustomBubbleMenu.displayName = 'CustomBubbleMenu';

CustomBubbleMenu.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    editor: PropTypes.object.isRequired,
};

export default CustomBubbleMenu;
